import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import {
  getSortedChartData,
  setupCursor,
  setupCursorEvents,
  createChartLegend,
  createTooltip,
  customizeAxisLabels,
  updateYAxisRange,
} from './chartHelpers';

export interface AnalysisChartOptions {
  chartLabel: string;
  chartData: any;
  parameterData: any;
  chartViewOptions: any;
  sortedData: any[];
  startDate?: Date;
  endDate?: Date;
}

export const createTestChart = (container: HTMLDivElement, options: AnalysisChartOptions): am5.Root | null => {
  const { chartLabel, parameterData, chartViewOptions, sortedData, startDate, endDate } = options;

  if (!sortedData || sortedData.length === 0 || !parameterData || parameterData.length === 0) {
    return null;
  }

  try {
    // Clear container
    container.innerHTML = '';

    // Create root
    const root = am5.Root.new(container);
    root.setThemes([am5themes_Dark.new(root)]);
    const { chartType, showTooltip } = chartViewOptions;

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 0,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 0,
        background: am5.Rectangle.new(root, {
          fill: am5.color('#122031'),
          fillOpacity: 0,
        }),
      }),
    );

    // X Axis
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: { timeUnit: 'second', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {
          strokeWidth: 1,
          strokeOpacity: 1,
          stroke: am5core.color('#4A5463'),
          minGridDistance: 80,
          pan: 'zoom',
        }),
      }),
    );

    if (startDate && endDate) {
      xAxis.setAll({
        min: new Date(startDate).getTime(),
        max: new Date(endDate).getTime(),
        strictMinMax: true,
      });
    }

    // Single Y Axis
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0.2,
        renderer: am5xy.AxisRendererY.new(root, {
          pan: 'zoom',
          strokeWidth: 1,
          strokeOpacity: 1,
          stroke: am5core.color('#4A5463'),
        }),
      }),
    );

    // Add Y-axis label
    const validSeries = sortedData.filter((d: any) => Array.isArray(d.dataPoints) && d.dataPoints.length > 0);

    if (validSeries.length > 0) {
      yAxis.children.unshift(
        am5.Label.new(root, {
          rotation: -90,
          text: chartLabel,
          y: am5.percent(50),
          centerX: am5.percent(50),
          textAlign: 'center',
          oversizedBehavior: 'wrap',
          maxHeight: 500,
          fontFamily: 'Mulish',
          fontSize: 12,
          fontWeight: '400',
          marginRight: -5,
          minWidth: 45,
        }),
      );
    }

    customizeAxisLabels(yAxis.get('renderer'), 14);

    // Store series and bullets
    const allSeries: am5xy.LineSeries[] = [];
    const bullets: am5.Bullet[] = [];

    // Add legend
    const legend = createChartLegend(chart, root);

    // Function to create cursor bullet for a series
    const createCursorBullet = (series: am5xy.LineSeries, markerColor: string | undefined) => {
      const bullet = am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 6,
          fill: am5.color('#323F46'),
          stroke: am5.color(markerColor || '#60BFDA'),
          strokeWidth: 4,
          visible: false,
        }),
      });
      series.bulletsContainer.children.push(bullet.get('sprite'));
      return bullet;
    };

    const createDynamicTooltip = () => {
      if (!showTooltip) return undefined;

      const tooltip = createTooltip('', root, {
        backgroundColor: '#001023',
        backgroundOpacity: 1,
        strokeColor: '#001023',
        textColor: '#FFFFFF',
        fontSize: 14,
        verticalPosition: 'center',
        chartHeight: chart.plotContainer.height(),
      });

      tooltip.adapters.add('labelText', (_text, target) => {
        const dataItem = target.dataItem;
        if (!dataItem) return '';

        const dataContext = dataItem.dataContext as any;
        if (!dataContext) return '';

        let tooltipContent = `[bold]{valueX.formatDate('MMM d, yyyy hh:mm a')}[/]`;

        chart.series.each((series) => {
          if (series.get('visible') === false) return;

          const seriesName = series.get('legendLabelText') || series.get('name');
          const color = (series as any).getPrivate('legendMarkerFill')?.toString() || '#FFFFFF';

          const matchedParam = parameterData.find(
            (param: any) => param.name.toLowerCase() === seriesName?.toLowerCase(),
          );

          const displayTitle = matchedParam?.title || seriesName;
          const units = matchedParam?.units || '';

          // Find the corresponding data point for this series
          const seriesData = series.data.values;
          const matchingPoint = seriesData.find((point: any) => Math.abs(point.date - dataContext.date) < 1000);

          if (matchingPoint && (matchingPoint as any).value !== undefined) {
            const valueWithUnits = units
              ? `${(matchingPoint as any).value} ${units}`
              : `${(matchingPoint as any).value}`;
            tooltipContent += `\n[${color}]●[/] ${displayTitle}: ${valueWithUnits}`;
          }
        });

        return tooltipContent;
      });

      return tooltip;
    };

    // Create series for each dataset
    sortedData.forEach((dataset: any) => {
      const matchedParam = parameterData.find(
        (param: any) => param.description.toLowerCase() === dataset.title.toLowerCase(),
      );

      // Only proceed if found a matching parameter
      if (!matchedParam) {
        return;
      }

      const lineColor = matchedParam?.lineColor || '#60BFDA';

      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: 'Line chart',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          valueXField: 'date',
          stroke: am5.color(lineColor),
        }),
      );

      series.strokes.template.setAll({ strokeWidth: 2 });

      const tooltip = createDynamicTooltip();
      if (showTooltip && tooltip) {
        series.set('tooltip', tooltip);
      }

      series.data.setAll(dataset.dataPoints);
      allSeries.push(series);

      // Add cursor bullet
      bullets.push(createCursorBullet(series, lineColor));

      // Add to legend
      legend.data.push(series);
      series.set('legendLabelText', matchedParam?.name);
      (series as any).setPrivate('legendMarkerFill', am5.color(lineColor));
    });

    legend.itemContainers.each((itemContainer) => {
      itemContainer.events.on('click', () => {
        setTimeout(() => updateYAxisRange(allSeries, yAxis), 0);
      });
    });

    updateYAxisRange(allSeries, yAxis);

    // Setup cursor and events
    const cursor = setupCursor(chart, root, xAxis, '#60BFDA', 2);
    setupCursorEvents(cursor, xAxis, chart, allSeries, bullets, sortedData, parameterData, chartType, {
      showTooltip: showTooltip,
      tooltipOffset: 20,
    });

    return root;
  } catch (e) {
    console.error('Error creating test chart:', e);
    return null;
  }
};

export const getSortedChartDataForTest = (chartData: any, parameterData: any) => {
  return getSortedChartData(chartData, parameterData);
};
